import { useCallback, useEffect, useRef, useState } from 'react'
import { soundManager } from '@core/audio/sounds/soundManager'

// Copy-to-clipboard with a short-lived "copied" acknowledgement.
//
// Shared by every place that offers a provably-fair value for copying, so the
// clipboard handling, the reset timing and the failure behaviour stay identical.
//
// Clipboard writes can be rejected (insecure context, denied permission). That
// is deliberately silent: the value is always rendered and selectable, so a
// failed copy is an inconvenience, not an error worth interrupting the player.
export function useCopyToClipboard({ resetAfterMs = 1500 } = {}) {
  const [copiedKey, setCopiedKey] = useState(null)
  const timerRef = useRef(null)

  // Clear a pending reset if the component unmounts first (popovers close fast).
  useEffect(() => () => clearTimeout(timerRef.current), [])

  const copy = useCallback(
    async (value, key = value) => {
      if (!value) return false

      soundManager.play('CLICK')

      try {
        await navigator.clipboard.writeText(value)
      } catch {
        return false
      }

      setCopiedKey(key)
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setCopiedKey(null), resetAfterMs)

      return true
    },
    [resetAfterMs]
  )

  return { copy, copiedKey, isCopied: (key) => copiedKey === key }
}
